import { useTranslation } from "react-i18next";

/**
 * Terms of Service, version 3.5 (effective 3 October 2026).
 * Canonical text shared by the public legal page, the authenticated portal
 * page and the in-app dialog. Section ids match the anchors the apps link to.
 */
export function TermsContent() {
  const { t } = useTranslation("common");
  return (
    <>
      <p className="text-sm text-muted-foreground mb-4">{t("web.legal.englishOnly")}</p>
      <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
      <p className="text-sm text-muted-foreground mb-1">
        Provided by Elefa Mobile Pty Ltd, ABN 16 694 346 088, Melbourne, Australia.
      </p>
      <p className="text-sm text-muted-foreground mb-8">Version 3.5 · Effective 3 October 2026</p>

      <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
        <section>
          <p className="text-muted-foreground">
            <strong>In short.</strong> elefa Connect is a prepaid app for calling phone numbers
            abroad over the internet. You buy calling credit; each call is charged at the rate
            shown before you dial. Purchased credit never expires. Bonus credit is a gift for your
            own calls. You can send purchased credit to another elefa customer; it can only be used
            for calls. Refunds follow the rules in section 7. elefa cannot call emergency services.
            Australian Consumer Law applies to everything below.
          </p>
        </section>

        <section>
          <h2 id="agreement" className="text-2xl font-semibold mb-4 scroll-mt-24">1. Agreement</h2>
          <p className="text-muted-foreground">
            <strong>These terms are a contract</strong> between you and Elefa Mobile Pty Ltd. They
            cover the elefa Connect apps, the website, and support. You accept them by creating an
            account, buying credit, or placing a call. If you do not accept them, do not use the
            service. The Privacy Policy is part of these terms.
          </p>
        </section>

        <section>
          <h2 id="definitions" className="text-2xl font-semibold mb-4 scroll-mt-24">2. Definitions</h2>
          <div className="text-muted-foreground space-y-2">
            <p><strong>Account:</strong> your registered elefa account, identified by your verified mobile number.</p>
            <p>
              <strong>Calling credit, or credit:</strong> a prepayment for calls made through elefa,
              held in your account in your wallet currency. It has no cash value, cannot be
              withdrawn, earns no interest, is not a loan and is not money. It can be used only for
              elefa's services.
            </p>
            <p>
              <strong>Purchased credit:</strong> credit you paid for. <strong>Bonus credit:</strong>{" "}
              credit we gave you. <strong>Received credit:</strong> credit another customer sent you.
            </p>
            <p>
              <strong>Wallet currency:</strong> the currency your account is kept in, Australian
              dollars or US dollars, set when you sign up by the country of your phone number.
            </p>
            <p><strong>Rate:</strong> the per-minute price of a call to a destination, shown before you dial.</p>
          </div>
        </section>

        <section>
          <h2 id="service" className="text-2xl font-semibold mb-4 scroll-mt-24">3. What elefa is</h2>
          <p className="text-muted-foreground">
            <strong>elefa Connect is a prepaid over-the-top voice service.</strong> Calls travel
            from your device over the internet to our network and are handed to the public telephone
            network for delivery. It works alongside your mobile or fixed-line service and does not
            replace it. Call quality depends on your internet connection and the destination network.
          </p>
          <p className="text-muted-foreground mt-4">
            We may add, change or withdraw features. If a change removes something you have paid for
            and not used, we refund that credit.
          </p>
        </section>

        <section>
          <h2 id="your-account" className="text-2xl font-semibold mb-4 scroll-mt-24">4. Your account</h2>
          <div className="text-muted-foreground space-y-4">
            <p>
              <strong>Who can open one.</strong> You must be 18 or older and provide a mobile number
              you are entitled to use and an email address. One account per person; accounts cannot
              be shared or transferred.
            </p>
            <p>
              <strong>Your email.</strong> We use it for two kinds of message. Service messages,
              which you receive while you have an account: receipts, notices about your account, and
              security alerts. And updates, which you can stop at any time: a monthly summary of your
              calls and spend, news about features and destinations, offers, and occasional
              invitations to tell us what to build. Every update carries an unsubscribe link; you can
              also opt out in Account settings or by emailing{" "}
              <a href="mailto:support@elefa.com.au" className="text-primary hover:underline">support@elefa.com.au</a>,
              and we act on it within 5 business days. Your email is not shown to other users and is
              never passed to anyone else for marketing.
            </p>
            <p>
              <strong>Security.</strong> You are responsible for what is done through your account.
              Tell us at{" "}
              <a href="mailto:security@elefa.com.au" className="text-primary hover:underline">security@elefa.com.au</a>{" "}
              if you think someone else has used it.
            </p>
            <p>
              <strong>Identity.</strong> Where the law or our security rules require it, we may ask
              you to verify your identity before allowing higher limits.
            </p>
          </div>
        </section>

        <section>
          <h2 id="credit" className="text-2xl font-semibold mb-4 scroll-mt-24">5. Credit, rates and payment</h2>
          <div className="text-muted-foreground space-y-4">
            <p>
              <strong>Prepaid.</strong> You buy credit before you call. Payments are taken by Stripe;
              we do not hold your card details. Prices for Australian customers include GST.
            </p>
            <p>
              <strong>Rates.</strong> The rate for each destination is shown in the app and on the
              dialler before you call. Rates change with carrier costs and exchange rates; the rate
              shown when you start a call is the rate for that call. Calls are charged in the billing
              increment shown for the destination, usually per minute.
            </p>
            <p>
              <strong>Purchased credit never expires.</strong> If your account has had no calls or
              top-ups for 24 months we will write to you; if we do not hear from you within 60 days
              we may close the account and refund the purchased credit to the card you paid with.
            </p>
            <p>
              <strong>Bonus credit</strong> is a gift for your own calls. It cannot be sent, refunded
              or withdrawn, it expires 90 days after it is given, and it is removed if the top-up
              that earned it is refunded or charged back.
            </p>
          </div>
        </section>

        <section>
          <h2 id="send-credit" className="text-2xl font-semibold mb-4 scroll-mt-24">
            6. Sending credit to another elefa customer
          </h2>
          <p className="text-muted-foreground">
            <strong>Send credit</strong> lets you send whole dollars of your purchased credit to
            another elefa customer by their phone number, from the Wallet.
          </p>
          <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
            <li>Only purchased credit can be sent, and only after it has been in your account for 3 days. Bonus and received credit stay with you for your own calls.</li>
            <li>Each send is between A$5 and A$50; you can send up to A$100 a day and A$300 a month, to at most 5 people a month. A person can receive up to A$300 a month.</li>
            <li>Credit you receive can be used for calls straight away and cannot be sent on.</li>
            <li>If the number you send to is not on elefa, we invite them by SMS and hold the credit for 30 days. If they have not joined by then, it comes back to you.</li>
            <li>A send is final once delivered. We reverse a send only where it was made in error or fraud, and we will tell you.</li>
            <li>You must have been on elefa for 3 days and made a paid call before your first send. Sending between accounts in different wallet currencies is not available yet. Send credit may not be available in some countries.</li>
            <li>Sent credit is calling credit only. It has no cash value, cannot be withdrawn or converted, and is not a money transfer. It cannot be used with elefa Money or any payment service.</li>
            <li>We may pause sending at any time to protect customers, and may set lower limits for an account where our security rules require it.</li>
          </ul>
        </section>

        <section>
          <h2 id="refunds" className="text-2xl font-semibold mb-4 scroll-mt-24">7. Refunds</h2>
          <p className="text-muted-foreground"><strong>We refund purchased credit</strong> when:</p>
          <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-2">
            <li>it cannot be used for what you bought it for, for example a destination we do not connect or a fault on our side;</li>
            <li>you were charged twice for one top-up;</li>
            <li>you ask within 14 days of a top-up and have not used any of that credit; or</li>
            <li>we withdraw a destination or close your account.</li>
          </ul>
          <div className="text-muted-foreground space-y-4 mt-4">
            <p>
              Used credit is not refunded for a change of mind. Bonus and received credit are not
              purchased and are not refunded.
            </p>
            <p>
              <strong>How.</strong> Refunds go to the card you paid with, in the currency you paid
              in. We submit them straight away; your bank usually shows them within 5 to 10 business
              days. A refund soon after a top-up may appear as the original charge disappearing
              rather than as a separate credit. If nothing has shown after 10 business days, tell us
              and we will give you the reference your bank needs to trace it.
            </p>
            <p>
              <strong>Charges you do not recognise.</strong> Tell us within 90 days so we can
              investigate. Contact us before raising a chargeback; a chargeback raised without
              contacting us may suspend your account while we look into it.
            </p>
            <p>
              <strong>Your rights.</strong> Nothing in this section limits your rights under the
              Australian Consumer Law. Our services come with guarantees that cannot be excluded: you
              are entitled to a refund or compensation for a major failure and to compensation for
              other reasonably foreseeable loss caused by a failure to meet a guarantee. If you live
              outside Australia, any non-excludable right your own law gives you also applies.
            </p>
            <p>
              To ask for a refund: Account, Help, Refunds, or email{" "}
              <a href="mailto:support@elefa.com.au" className="text-primary hover:underline">support@elefa.com.au</a>{" "}
              with your phone number and the date of the top-up.
            </p>
          </div>
        </section>

        <section>
          <h2 id="destinations" className="text-2xl font-semibold mb-4 scroll-mt-24">8. Where you can call</h2>
          <div className="text-muted-foreground space-y-4">
            <p>
              <strong>Destinations.</strong> elefa connects calls to the destinations in the rates
              list. Some show "Coming soon" and cannot be called yet; calls to the United States and
              Canada are among them while we complete the checks that market requires. We may block
              numbers or ranges used for fraud. You are not charged for a call that does not connect.
            </p>
            <p>
              <strong>Countries that restrict internet calling.</strong> Some countries restrict or
              block over-the-top voice services. You are responsible for confirming that using elefa
              is lawful where you are.
            </p>
          </div>
        </section>

        <section>
          <h2 id="emergency" className="text-2xl font-semibold mb-4 scroll-mt-24">9. Emergency calls</h2>
          <p className="text-muted-foreground">
            <strong>elefa cannot call emergency services.</strong> To reach emergency services use
            your phone's own dialler: 000 in Australia, 112 on any mobile, 911 in North America, 999
            or 111 where those apply. elefa does not send your location to emergency operators, may
            not work in a power or internet outage, and must not be relied on in an emergency. Keep a
            normal phone service available and tell anyone who uses your account.
          </p>
        </section>

        <section>
          <h2 id="fair-use" className="text-2xl font-semibold mb-4 scroll-mt-24">10. Fair use</h2>
          <div className="text-muted-foreground space-y-4">
            <p>
              <strong>Use elefa as a person, for your own calls.</strong> You must not use it for
              fraud, spam, automated or bulk calling, call-centre or resale traffic, or to harass or
              deceive anyone; to call premium-rate numbers; to probe, overload or interfere with the
              service; or from a country whose law prohibits it. Bonus credit is for calling home,
              not for testing routes.
            </p>
            <p>
              <strong>What we do.</strong> We may limit what an account can call, pause sending, or
              suspend the account when our security rules find misuse, and we will tell you. You can
              ask support to review it. Suspension for misuse does not carry a refund of bonus
              credit; purchased credit is handled under section 7.
            </p>
          </div>
        </section>

        <section>
          <h2 id="availability" className="text-2xl font-semibold mb-4 scroll-mt-24">11. Availability and quality</h2>
          <p className="text-muted-foreground">
            <strong>Internet required.</strong> The service needs a working internet connection;
            quality follows the connection and the destination network. We may carry out planned
            maintenance and will give notice of significant outages where we can. We do not promise
            that the service will be uninterrupted.
          </p>
        </section>

        <section>
          <h2 id="privacy" className="text-2xl font-semibold mb-4 scroll-mt-24">12. Privacy and security</h2>
          <p className="text-muted-foreground">
            <strong>How we handle personal information</strong> is in the Privacy Policy. Traffic
            between your device and our network is encrypted in transit; the leg carried by the
            public telephone network cannot be, by its nature.
          </p>
        </section>

        <section>
          <h2 id="termination" className="text-2xl font-semibold mb-4 scroll-mt-24">
            13. Suspension and closing your account
          </h2>
          <p className="text-muted-foreground">
            <strong>You may close your account</strong> at any time in the app or by writing to{" "}
            <a href="mailto:support@elefa.com.au" className="text-primary hover:underline">support@elefa.com.au</a>;
            purchased credit is refunded under section 7.{" "}
            <strong>We may suspend or close your account</strong> if you breach these terms, if we
            reasonably suspect fraud or unlawful use, if a payment is reversed or charged back, or if
            continuing would expose us or others to legal risk. We give notice where it is reasonable
            and lawful to do so. On closure, bonus credit is forfeited.
          </p>
        </section>

        <section>
          <h2 id="ip" className="text-2xl font-semibold mb-4 scroll-mt-24">14. Intellectual property</h2>
          <p className="text-muted-foreground">
            <strong>The service, its software and its marks belong to elefa.</strong> You may install
            and use the app on devices you control for your own use. You may not copy, modify,
            reverse engineer, resell or distribute it.
          </p>
        </section>

        <section>
          <h2 id="third-parties" className="text-2xl font-semibold mb-4 scroll-mt-24">15. Third parties</h2>
          <p className="text-muted-foreground">
            <strong>The service relies on</strong> the App Store, Google Play, payment processors,
            messaging providers and interconnect carriers. Their terms apply to your use of their
            platforms. Apple has no obligation to support the app.
          </p>
        </section>

        <section>
          <h2 id="liability" className="text-2xl font-semibold mb-4 scroll-mt-24">16. Liability</h2>
          <p className="text-muted-foreground">
            <strong>To the extent the law allows,</strong> our total liability to you in any 12
            months is limited to the amount you paid us in that period or A$100, whichever is
            greater, and neither of us is liable to the other for indirect or consequential loss.
            Nothing here limits any liability that the law does not allow to be limited, including
            your rights under the Australian Consumer Law and liability for death or personal injury
            caused by negligence.
          </p>
        </section>

        <section>
          <h2 id="indemnity" className="text-2xl font-semibold mb-4 scroll-mt-24">17. Indemnity</h2>
          <p className="text-muted-foreground">
            <strong>You indemnify us</strong> for loss caused by your breach of these terms, your
            misuse of the service or your breach of any law, except where the loss is our fault or
            where the indemnity would be inconsistent with consumer law.
          </p>
        </section>

        <section>
          <h2 id="force-majeure" className="text-2xl font-semibold mb-4 scroll-mt-24">18. Events beyond our control</h2>
          <p className="text-muted-foreground">
            <strong>Neither of us is liable</strong> for failure or delay caused by events beyond our
            reasonable control, including carrier outages, internet failures, government action,
            disaster or war.
          </p>
        </section>

        <section>
          <h2 id="complaints" className="text-2xl font-semibold mb-4 scroll-mt-24">19. Complaints</h2>
          <p className="text-muted-foreground">
            <strong>If something goes wrong,</strong> email{" "}
            <a href="mailto:complaints@elefa.com.au" className="text-primary hover:underline">complaints@elefa.com.au</a>{" "}
            or use Help in the app. We acknowledge complaints within 2 working days and aim to
            resolve them within 15 working days. elefa is not a member of the Telecommunications
            Industry Ombudsman scheme; we will join when our service profile requires it. If you are
            not satisfied: privacy matters can go to the Office of the Australian Information
            Commissioner; a payment dispute can be raised with your card issuer; industry concerns
            with the Australian Communications and Media Authority. Outside Australia, your own
            consumer or data protection regulator.
          </p>
        </section>

        <section>
          <h2 id="law" className="text-2xl font-semibold mb-4 scroll-mt-24">20. Governing law</h2>
          <p className="text-muted-foreground">
            <strong>Victoria, Australia.</strong> These terms are governed by the laws of Victoria
            and disputes go to its courts, after our complaints process. If the law of your country
            gives you a non-excludable right to bring proceedings there, that right stands.
          </p>
        </section>

        <section>
          <h2 id="general" className="text-2xl font-semibold mb-4 scroll-mt-24">21. General</h2>
          <p className="text-muted-foreground">
            <strong>Changes.</strong> We may update these terms. Material changes are notified by
            email, in the app or on the website before they take effect; continuing to use the
            service after that date is acceptance. <strong>Entire agreement.</strong> These terms and
            the Privacy Policy are the whole agreement. <strong>Assignment.</strong> You may not
            assign these terms; we may assign them to an affiliate or a successor.{" "}
            <strong>Severability and waiver.</strong> An invalid provision does not affect the rest;
            not enforcing a provision is not a waiver. <strong>Notices.</strong> We give notice by
            email, in the app or on the website; you give notice to{" "}
            <a href="mailto:legal@elefa.com.au" className="text-primary hover:underline">legal@elefa.com.au</a>.
          </p>
        </section>

        <section>
          <h2 id="contact" className="text-2xl font-semibold mb-4 scroll-mt-24">22. Contact</h2>
          <p className="text-muted-foreground">
            Elefa Mobile Pty Ltd, ABN 16 694 346 088, Melbourne, Australia.
          </p>
          <p className="text-muted-foreground mt-2">
            Support:{" "}
            <a href="mailto:support@elefa.com.au" className="text-primary hover:underline">support@elefa.com.au</a> ·
            Complaints:{" "}
            <a href="mailto:complaints@elefa.com.au" className="text-primary hover:underline">complaints@elefa.com.au</a> ·
            Security:{" "}
            <a href="mailto:security@elefa.com.au" className="text-primary hover:underline">security@elefa.com.au</a> ·
            Legal:{" "}
            <a href="mailto:legal@elefa.com.au" className="text-primary hover:underline">legal@elefa.com.au</a>
          </p>
        </section>
      </div>
    </>
  );
}
